import { type ReactNode, useState, useRef, useEffect } from "react"
import { useCurrency } from "@/context/currency/currency-context.ts"
import { Pencil, Check, X, Loader2, AlertTriangle } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"

interface DashboardHeaderProps {
	totalValue: number
	title?: string
	subtitle?: string
	children?: ReactNode
	onTitleEdit?: (newTitle: string) => Promise<void>
	loading?: boolean
	cashBalance?: number
	cashCurrency?: string
	cashEnabled?: boolean
	/** Set when cash tracking is enabled but the balance fetch failed (e.g. missing FX rate). */
	cashError?: string
}

export function DashboardHeader({
	totalValue,
	title = "My Hive",
	subtitle = "Manage your honeycombs and track growth.",
	children,
	onTitleEdit,
	loading,
	cashBalance,
	cashCurrency,
	cashEnabled,
	cashError,
}: DashboardHeaderProps) {
	const { format, convert } = useCurrency()
	const [isEditing, setIsEditing] = useState(false)
	const [editValue, setEditValue] = useState(title)
	const [isSaving, setIsSaving] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus()
			inputRef.current.select()
		}
	}, [isEditing])

	const handleSave = async () => {
		const trimmed = editValue.trim()
		if (!trimmed || trimmed === title) {
			setIsEditing(false)
			setEditValue(title)
			return
		}
		setIsSaving(true)
		try {
			await onTitleEdit?.(trimmed)
			setIsEditing(false)
		} catch {
			setEditValue(title)
			setIsEditing(false)
		} finally {
			setIsSaving(false)
		}
	}

	const handleCancel = () => {
		setEditValue(title)
		setIsEditing(false)
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") handleSave()
		if (e.key === "Escape") handleCancel()
	}

	// Compute display values when cash tracking is enabled
	const cashConverted = cashEnabled && cashBalance != null && cashCurrency ? convert(cashBalance, cashCurrency) : null
	const investedValue = totalValue // positions value passed in from parent
	const totalNetWorth = investedValue + (cashConverted ?? 0)
	const showCashBreakdown = cashEnabled && cashConverted != null
	// A non-finite total means at least one position used a currency with no known
	// exchange rate (convert() returns NaN rather than silently passing through 1:1).
	// Surface this loudly instead of rendering a broken "NaN" figure.
	const fxError = !Number.isFinite(totalNetWorth)

	return (
		<div className="flex flex-col md:flex-row justify-between md:items-end border-b border-sky-900/10 dark:border-sky-100/10 pb-4 gap-2 md:gap-0">
			<div>
				{isEditing ? (
					<div className="flex items-center gap-2">
						<input
							ref={inputRef}
							value={editValue}
							onChange={(e) => setEditValue(e.target.value)}
							onKeyDown={handleKeyDown}
							className="text-2xl sm:text-3xl font-bold text-sky-950 dark:text-sky-50 bg-transparent border-b-2 border-sky-400 outline-none px-0 py-0 w-full max-w-xs"
							disabled={isSaving}
						/>
						{isSaving ? (
							<Loader2 className="h-5 w-5 animate-spin text-sky-600" />
						) : (
							<>
								<button
									onClick={handleSave}
									className="p-1 rounded hover:bg-sky-100 dark:hover:bg-sky-900/30 text-green-600 dark:text-green-400"
								>
									<Check className="h-5 w-5" />
								</button>
								<button
									onClick={handleCancel}
									className="p-1 rounded hover:bg-sky-100 dark:hover:bg-sky-900/30 text-red-500"
								>
									<X className="h-5 w-5" />
								</button>
							</>
						)}
					</div>
				) : (
					<div className="flex items-center gap-2 group">
						<h1 className="text-3xl font-bold text-sky-950 dark:text-sky-50">{title}</h1>
						{onTitleEdit && (
							<button
								onClick={() => {
									setEditValue(title)
									setIsEditing(true)
								}}
								className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-sky-100 dark:hover:bg-sky-900/30 transition-opacity text-muted-foreground hover:text-sky-600"
							>
								<Pencil className="h-4 w-4" />
							</button>
						)}
					</div>
				)}
				<p className="text-muted-foreground">{subtitle}</p>
			</div>

			<div className="self-end md:self-auto text-right">
				{children && <div className="flex items-center justify-end gap-2 mb-1 md:mb-0 md:mr-4">{children}</div>}
				<p className="text-sm font-medium text-muted-foreground">Total Net Worth</p>
				{loading ? (
					<Skeleton className="h-10 w-40 mt-1" />
				) : fxError ? (
					<>
						<p className="text-3xl sm:text-4xl font-extrabold text-amber-600 dark:text-amber-400">—</p>
						<p className="flex items-center justify-end gap-1 text-xs text-amber-600 dark:text-amber-400 mt-0.5">
							<AlertTriangle className="h-3 w-3 flex-shrink-0" />
							Exchange rate unavailable
						</p>
					</>
				) : (
					<>
						<p className="text-3xl sm:text-4xl font-extrabold text-sky-600 dark:text-sky-400">
							{format(totalNetWorth, { skipConversion: true })}
						</p>
						{showCashBreakdown && (
							<p className="text-sm text-muted-foreground mt-0.5">
								{format(investedValue, { skipConversion: true })} Invested
								{" · "}
								{format(cashConverted!, { skipConversion: true })} Cash
							</p>
						)}
						{cashError && !showCashBreakdown && (
							<p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-0.5">
								<AlertTriangle className="h-3 w-3 flex-shrink-0" />
								Cash balance unavailable
							</p>
						)}
					</>
				)}
			</div>
		</div>
	)
}
